import { MailWarning, X } from 'lucide-react';
import { useState } from 'react';

import { ChangeEmailDialog } from '@/components/account/change-email-dialog';
import { Button } from '@/components/ui/button';
import { BANNER_CLAIMS_POLL_MS, useClaimsRefreshPoll } from '@/hooks/use-claims-refresh-poll';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useEmailVerificationState } from '@/hooks/use-email-verification-state';
import { useVerificationResend } from '@/hooks/use-verification-resend';

const DISMISSED_KEY = 'escloud-verification-banner-dismissed';

function readDismissed(): boolean {
    try {
        return sessionStorage.getItem(DISMISSED_KEY) === '1';
    } catch {
        return false;
    }
}

/**
 * The visible half of the soft email gate (ESCLOUD-333). Verification no
 * longer blocks any screen: this banner asks for it, and the device-use gate
 * enforces it before first clinical use (server-side too). Renders only on a
 * PROVEN unverified claim: `unprovable` is a session problem, not evidence
 * about the address, and `pending` is not an answer yet.
 *
 * `pending` keeps the previous verdict rather than hiding the banner: the
 * banner's own poll drops the claim to `pending` on every refresh, and
 * unmounting on it restarted the poll — whose first tick is immediate — the
 * moment each refresh settled, hammering the token endpoint in a tight loop.
 * The last verdict is state adjusted during render (not a ref, not an effect):
 * concurrent-render safe, and no frame renders the stale verdict.
 */
export function EmailVerificationBanner() {
    const state = useEmailVerificationState();
    const [settled, setSettled] = useState(state);
    if (state !== 'pending' && state !== settled) setSettled(state);
    if (settled !== 'unverified') return null;
    return <UnverifiedBanner />;
}

function UnverifiedBanner() {
    const { data: user } = useCurrentUser();
    const [dismissed, setDismissed] = useState(readDismissed);
    const [changing, setChanging] = useState(false);
    const email = user?.email ?? '';
    const { resend, isPending } = useVerificationResend({ email });

    // The token minted at sign-in cannot know about a click in the inbox.
    useClaimsRefreshPoll(BANNER_CLAIMS_POLL_MS);

    if (dismissed) return null;

    const dismiss = () => {
        try {
            sessionStorage.setItem(DISMISSED_KEY, '1');
        } catch {
            /* session-only convenience */
        }
        setDismissed(true);
    };

    return (
        <>
            <div
                role="status"
                className="flex items-center gap-3 border-b border-primary/30 bg-primary/10 px-4 py-2 text-sm"
            >
                <MailWarning className="size-4 shrink-0 text-primary" aria-hidden />
                <p className="min-w-0 flex-1">
                    <span className="font-medium">Verify your email address.</span>{' '}
                    <span className="text-muted-foreground">
                        We sent a link to {email}. Verification is required before your first clinical use.
                    </span>
                </p>
                <Button
                    variant="link"
                    size="sm"
                    className="h-auto p-0"
                    onClick={() => void resend()}
                    disabled={isPending}
                >
                    {isPending ? 'Requesting…' : 'Resend'}
                </Button>
                <Button variant="link" size="sm" className="h-auto p-0" onClick={() => setChanging(true)}>
                    Change address
                </Button>
                <Button variant="ghost" size="icon" className="size-7" onClick={dismiss} aria-label="Dismiss">
                    <X className="size-4" aria-hidden />
                </Button>
            </div>
            <ChangeEmailDialog open={changing} onOpenChange={setChanging} currentEmail={email} />
        </>
    );
}
